import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import {
  IEventoGenerico,
  IPersonalSalud,
  IUsuario,
  IValoresEventoEmergencia,
} from 'modelos/src';
import { Subscription, from, switchMap, timer } from 'rxjs';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { EventoDetallesModalComponent } from '../detalles-eventos-emergencias/detalles.component';
import { EmergenciasService } from '../../../../auxiliares/servicios/http/emergencias.service';
import {
  IPresentacion,
  ESTADOS_FINALES,
  presentacionEstado,
} from '../../../entidades/emergencias/estado-emergencia';
import {
  fechaHoraEmergencia as fechaHora,
  haceCuanto,
} from '../../../entidades/emergencias/emergencias/emergencia.service';

/**
 * Un paso de la línea de tiempo. Los estados que el tracker repite
 * ("En tránsito", "Rumbo a hospital") se agrupan en uno solo.
 */
export type IEventoProcesado = IEventoGenerico & {
  eventosPrimario: IEventoGenerico;
  eventosSecundarios: IEventoGenerico[];
  totalEventos: number;
  timestampInicio?: string;
  timestampFin?: string;
};

/** Lo que muestra cada ficha, precalculado (no en el template). */
interface FichaSeguimiento {
  evento: IEventoProcesado;
  presentacion: IPresentacion;
  fecha: string;
  hace: string;
  rango?: string;
  lineas: { icono: string; texto: string }[];
}

const ESTADOS_REPETITIVOS = ['En tránsito', 'Rumbo a hospital'];

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-eventos-emergencias',
  templateUrl: './seguimiento.component.html',
  styleUrl: './seguimiento.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SeguimientoEmergenciasComponent implements OnChanges, OnDestroy {
  private emergenciasService = inject(EmergenciasService);
  private formularios = inject(FormularioDialogService);

  /** Id de la emergencia. */
  @Input() public id = '';

  public cargando = true;
  public error = false;
  public fichas: FichaSeguimiento[] = [];
  private eventos$?: Subscription;

  // El endpoint propio del módulo (no /eventos-genericos, que exige permisos
  // de Eventos): trae ya el populate acotado, sin datos del cliente.
  private listar(): void {
    this.eventos$?.unsubscribe();
    this.cargando = true;
    this.error = false;
    this.eventos$ = timer(0, 30000)
      .pipe(switchMap(() => from(this.emergenciasService.seguimiento(this.id))))
      .subscribe({
        next: (eventos) => {
          this.fichas = this.agrupar(eventos || []).map((e) => this.ficha(e));
          this.cargando = false;
        },
        error: (err) => {
          console.error(err);
          this.error = true;
          this.cargando = false;
        },
      });
  }

  /** Agrupa los estados consecutivos que el tracker repite. */
  private agrupar(eventos: IEventoGenerico[]): IEventoProcesado[] {
    const agrupados: IEventoProcesado[] = [];
    let i = 0;
    while (i < eventos.length) {
      const actual = eventos[i];
      if (ESTADOS_REPETITIVOS.includes(actual.estado || '')) {
        const grupo: IEventoGenerico[] = [];
        let j = i;
        while (j < eventos.length && eventos[j].estado === actual.estado) {
          grupo.push(eventos[j]);
          j++;
        }
        // Vienen del más nuevo al más viejo
        agrupados.push({
          ...actual,
          eventosPrimario: grupo[grupo.length - 1],
          eventosSecundarios: grupo.slice(0, -1).reverse(),
          totalEventos: grupo.length,
          timestampInicio: grupo[grupo.length - 1].fechaCreacion,
          timestampFin: grupo[0].fechaCreacion,
        });
        i = j;
      } else {
        agrupados.push({
          ...actual,
          eventosPrimario: actual,
          eventosSecundarios: [],
          totalEventos: 1,
        });
        i++;
      }
    }
    return agrupados;
  }

  private ficha(evento: IEventoProcesado): FichaSeguimiento {
    const det = evento.detallesEmergencias;
    const valores = evento.valores as IValoresEventoEmergencia | undefined;
    const lineas: { icono: string; texto: string }[] = [];

    const movil = det?.movilAsignado?.identificacion;
    if (movil)
      lineas.push({ icono: 'airport_shuttle', texto: `Móvil ${movil}` });
    const chofer = this.nombreUsuario(det?.chofer);
    if (chofer)
      lineas.push({ icono: 'account_box', texto: `Chofer: ${chofer}` });
    const medicos = this.nombres(det?.medicos);
    if (medicos) {
      lineas.push({ icono: 'medical_services', texto: `Médicos: ${medicos}` });
    }
    const enfermeros = this.nombres(det?.enfermeros);
    if (enfermeros) {
      lineas.push({
        icono: 'medical_services',
        texto: `Enfermeros: ${enfermeros}`,
      });
    }
    if (det?.hospital?.identificacion) {
      lineas.push({
        icono: 'local_hospital',
        texto: `Hospital: ${det.hospital.identificacion}`,
      });
    }
    if (valores?.motivoReasignacion) {
      lineas.push({
        icono: 'swap_horiz',
        texto: `Motivo: ${valores.motivoReasignacion}`,
      });
    }
    if (valores?.motivoCancelacion) {
      lineas.push({
        icono: 'block',
        texto: `Motivo: ${valores.motivoCancelacion}`,
      });
    }
    if (valores?.diagnostico) {
      lineas.push({
        icono: 'medical_information',
        texto: `Diagnóstico: ${valores.diagnostico}`,
      });
    }
    if (valores?.observaciones) {
      lineas.push({ icono: 'notes', texto: valores.observaciones });
    }
    const responsable = this.nombreUsuario(det?.usuarioResponsable);
    if (responsable) {
      lineas.push({ icono: 'person', texto: `Registró: ${responsable}` });
    }

    const agrupado = evento.totalEventos > 1;
    const fechaRef = agrupado ? evento.timestampFin : evento.fechaCreacion;
    return {
      evento,
      presentacion: presentacionEstado(evento.estado),
      fecha: fechaHora(fechaRef),
      hace: haceCuanto(fechaRef),
      rango: agrupado
        ? `${evento.totalEventos.toLocaleString('es-AR')} reportes del móvil, desde ${fechaHora(evento.timestampInicio)} hasta ${fechaHora(evento.timestampFin)}`
        : undefined,
      lineas,
    };
  }

  private nombreUsuario(u?: IUsuario): string {
    if (!u) return '';
    const dp = u.datosPersonales;
    return dp?.nombre || u.usuario || '';
  }

  private nombres(personal?: IPersonalSalud[]): string {
    return (personal || [])
      .map((p) => p?.nombre)
      .filter(Boolean)
      .join(', ');
  }

  public esFinal(f: FichaSeguimiento): boolean {
    return ESTADOS_FINALES.includes(f.evento.estado as never);
  }

  public verDetalles(f: FichaSeguimiento): void {
    this.formularios.abrir(EventoDetallesModalComponent, {
      tamano: 'mediano',
      data: { evento: f.evento },
      cerrarAfuera: true,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && this.id) this.listar();
  }

  ngOnDestroy(): void {
    this.eventos$?.unsubscribe();
  }
}
